import { Link, useNavigate, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Book, type MemberProfile, type RatingsResponse } from "../api";
import { BookCover } from "../components/BookCover";
import { BookSections } from "../components/BookSections";
import { ReadingProgress } from "../components/ReadingProgress";
import { BookRatings } from "../components/BookRatings";
import { Avatar } from "../components/Avatar";
import { Badge, Button, Card, Ornament, Spinner } from "../components/ui";
import { StarRating } from "../components/StarRating";
import { GenerateAllButton } from "../components/GenerateAllButton";
import { PickerEditor } from "../components/PickerEditor";
import { SectionNav, SectionIcons, type SectionNavItem } from "../components/SectionNav";

const BOOK_SECTIONS: SectionNavItem[] = [
  { id: "section-top", label: "Top of page", icon: SectionIcons.hero },
  { id: "section-progress", label: "Reading progress", icon: SectionIcons.progress },
  { id: "section-ratings", label: "Ratings & reviews", icon: SectionIcons.ratings },
  { id: "section-context", label: "Author & moment", icon: SectionIcons.context },
  { id: "section-themes", label: "Themes", icon: SectionIcons.themes },
  { id: "section-passages", label: "Key passages", icon: SectionIcons.passages },
  { id: "section-chapters", label: "Chapter walk-through", icon: SectionIcons.chapters },
  { id: "section-notes", label: "Chapter notes", icon: SectionIcons.notes },
  { id: "section-questions", label: "Discussion questions", icon: SectionIcons.questions },
  { id: "section-characters", label: "Characters", icon: SectionIcons.characters },
  { id: "section-relationships", label: "Constellations", icon: SectionIcons.relationships },
  { id: "section-venue", label: "Venue", icon: SectionIcons.venue },
  { id: "section-merch", label: "Merch", icon: SectionIcons.merch },
  { id: "section-pairings", label: "Food & drink", icon: SectionIcons.pairings },
];

export default function BookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const bookId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const book = useQuery({
    queryKey: ["book", bookId],
    queryFn: () => api.get<{ book: Book }>(`/books/${bookId}`),
    enabled: !!bookId,
  });

  const current = useQuery({
    queryKey: ["current-book"],
    queryFn: () => api.get<{ book: Book | null }>("/books/current"),
  });

  const ratings = useQuery({
    queryKey: ["ratings", bookId],
    queryFn: () => api.get<RatingsResponse>(`/ratings/${bookId}`),
    enabled: !!bookId,
  });

  const members = useQuery({
    queryKey: ["members"],
    queryFn: () => api.get<{ members: MemberProfile[] }>("/members"),
  });

  const setCurrent = useMutation({
    mutationFn: () => api.post(`/books/${bookId}/set-current`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-book"] });
    },
  });

  const finish = useMutation({
    mutationFn: () => api.post(`/books/${bookId}/finish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["book", bookId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/books/${bookId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["books"] });
      qc.invalidateQueries({ queryKey: ["current-book"] });
      navigate("/library");
    },
  });

  if (book.isLoading) return <Spinner />;
  if (!book.data?.book) return <p className="text-plum-soft">Book not found.</p>;

  const b = book.data.book;
  const isCurrent = current.data?.book?.id === b.id;
  const picker = members.data?.members.find((m) => m.id === b.addedByUserId);
  const addedAtDate = b.addedAt ? format(parseISO(b.addedAt.replace(" ", "T")), "MMMM yyyy") : null;

  return (
    <div className="flex flex-col gap-12">
      <SectionNav sections={BOOK_SECTIONS} />
      <Link to="/library" className="text-sm text-plum-mute hover:text-rose w-fit">
        ← Library
      </Link>

      {/* Hero */}
      <section id="section-top" className="relative scroll-mt-24">
        <div className="absolute -top-6 -left-6 -right-6 -bottom-6 bg-gradient-to-br from-blush-soft/30 via-cream to-cream rounded-3xl -z-10" />
        <div className="flex flex-col md:flex-row gap-10 items-center md:items-start py-4">
          <BookCover url={b.coverUrl} title={b.title} author={b.author} size="xl" />
          <div className="flex-1 flex flex-col gap-4 text-center md:text-left">
            <div className="flex gap-2 justify-center md:justify-start flex-wrap">
              {isCurrent ? <Badge tone="rose">Currently Reading</Badge> : null}
              {(b as Book & { finishedAt?: string | null }).finishedAt ? (
                <Badge tone="gold">Finished</Badge>
              ) : null}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-plum leading-[1.05] tracking-tight">
              {b.title}
            </h1>
            <p className="font-serif italic text-xl text-plum-soft">by {b.author}</p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start items-center text-sm text-plum-soft">
              {b.pageCount ? <span>{b.pageCount} pages</span> : null}
              {b.isbn ? (
                <>
                  <span className="text-plum-mute">·</span>
                  <span className="font-mono text-xs tracking-wide">{b.isbn}</span>
                </>
              ) : null}
              {addedAtDate ? (
                <>
                  <span className="text-plum-mute">·</span>
                  <span>Added {addedAtDate}</span>
                </>
              ) : null}
              <AudibleButton title={b.title} author={b.author} />
            </div>

            {/* Picker + club rating */}
            <div className="flex items-center gap-5 flex-wrap justify-center md:justify-start mt-2">
              <PickerEditor
                bookId={b.id}
                picker={picker}
                members={members.data?.members ?? []}
              />
              {ratings.data?.average ? (
                <div className="flex items-center gap-2">
                  <StarRating value={ratings.data.average} readOnly size={16} />
                  <span className="text-xs text-plum-mute">
                    {ratings.data.average.toFixed(1)} · {ratings.data.count}
                  </span>
                </div>
              ) : null}
            </div>

            {b.summary ? (
              <p className="text-plum-soft leading-relaxed mt-2 max-w-2xl">{b.summary}</p>
            ) : null}

            <div className="flex flex-wrap gap-3 justify-center md:justify-start mt-4">
              <GenerateAllButton bookId={b.id} variant="primary" size="sm" />
              {!isCurrent ? (
                <Button onClick={() => setCurrent.mutate()} loading={setCurrent.isPending}>
                  Set as current book
                </Button>
              ) : null}
              {!(b as Book & { finishedAt?: string | null }).finishedAt ? (
                <Button variant="gold" size="sm" onClick={() => finish.mutate()} loading={finish.isPending}>
                  Mark as finished
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (confirm(`Remove "${b.title}" from the library? You can restore it later from the Library page.`)) {
                    remove.mutate();
                  }
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section id="section-progress" className="scroll-mt-24">
        <ReadingProgress bookId={b.id} totalChapters={null} />
      </section>

      <section id="section-ratings" className="scroll-mt-24">
        <BookRatings bookId={b.id} />
      </section>

      <Ornament />

      <BookSections bookId={b.id} />
    </div>
  );
}

// Tiny inline Audible search button — sits in the book's metadata strip
// alongside page count + ISBN. Replaces the standalone "Listen along" card.
function AudibleButton({ title, author }: { title: string; author: string }) {
  const q = encodeURIComponent(`${title} ${author}`);
  return (
    <>
      <span className="text-plum-mute">·</span>
      <a
        href={`https://www.audible.com/search?keywords=${q}`}
        target="_blank"
        rel="noreferrer noopener"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-bordeaux text-bordeaux hover:bg-bordeaux hover:text-rose-paper font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
        title="Search this book on Audible"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M3 18v-7a9 9 0 1 1 18 0v7M3 18a2 2 0 0 0 2 2h2v-7H5a2 2 0 0 0-2 2zM21 18a2 2 0 0 1-2 2h-2v-7h2a2 2 0 0 1 2 2z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
        Audible
      </a>
    </>
  );
}
